<template>
    <VueDatePicker
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
        :uid="id"
        :range="range"
        :dark="isDark"
        :clearable="clearable"
        :preset-dates="presetDates"
        :teleport="teleport"
        :placeholder="placeholder"
        :locale="$i18n.locale"
        :enable-time-picker="false"
        model-type="yyyy-MM-dd"
        format="yyyy-MM-dd"
        auto-apply
    />
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useTheme } from '@/composables/useTheme';
import { PRESETS } from '@/composables/useExpensesList';

// The one date picker of the app: every option that never changes lives here, so a screen only
// says what is different. The look (theme colors, height, border) is the CSS at the end of this file.
//
//   v-model     'yyyy-MM-dd' string, or [start, end] of them when `range`; null while empty
//   range       pick a period instead of a single day
//   clearable   shows the X that empties it (the model then becomes null)
//   quick-presets  lists This Month / Last 3 Months / This Year beside the calendar (needs `range`)
//   teleport    render the popup in <body>; needed inside a modal, whose overflow would cut it
//   id          set it to the same value as the <label for>
export default {
    components: { VueDatePicker },
    props: {
        modelValue: {
            type: [String, Array],
            default: null,
        },
        id: {
            type: String,
            default: undefined,
        },
        range: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        quickPresets: {
            type: Boolean,
            default: false,
        },
        teleport: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue'],
    computed: {
        // Computed on use so "this month"/"this year" are always the current ones
        presetDates() {
            if (!this.quickPresets) return [];
            const preset = (key, label) => {
                const { startDate, endDate } = PRESETS[key]();
                return { label: this.$t(label), value: [new Date(`${startDate}T00:00`), new Date(`${endDate}T00:00`)] };
            };
            return [
                preset('thisMonth', 'filters.presetThisMonth'),
                preset('last3Months', 'filters.presetLast3Months'),
                preset('thisYear', 'filters.presetThisYear'),
            ];
        },
    },
    setup() {
        const { isDark } = useTheme();
        return { isDark };
    },
};
</script>

<style>
/* Date picker colors come from the app's theme variables, so it follows dark/light mode
   (the library only ships fixed light and dark palettes) */
:root .dp__theme_dark,
:root .dp__theme_light {
    --dp-background-color: rgb(var(--c-gray-900));
    --dp-text-color: rgb(var(--c-fg));
    --dp-hover-color: rgb(var(--c-gray-800));
    --dp-hover-text-color: rgb(var(--c-fg));
    --dp-hover-icon-color: rgb(var(--c-gray-400));
    --dp-primary-color: #0369a1; /* sky-700 */
    --dp-primary-disabled-color: #0c4a6e;
    --dp-primary-text-color: #fff;
    --dp-secondary-color: rgb(var(--c-gray-400));
    --dp-border-color: rgb(var(--c-gray-700));
    --dp-menu-border-color: rgb(var(--c-gray-700));
    --dp-border-color-hover: rgb(var(--c-gray-400));
    --dp-border-color-focus: #0369a1;
    --dp-disabled-color: rgb(var(--c-gray-800));
    --dp-disabled-color-text: rgb(var(--c-gray-500));
    --dp-scroll-bar-background: rgb(var(--c-gray-800));
    --dp-scroll-bar-color: rgb(var(--c-gray-500));
    --dp-icon-color: rgb(var(--c-gray-400));
    --dp-range-between-dates-background-color: rgb(var(--c-gray-800));
    --dp-range-between-dates-text-color: rgb(var(--c-fg));
    --dp-range-between-border-color: rgb(var(--c-gray-700));
    --dp-border-radius: 0.375rem; /* rounded-md, like the selects */
}

/* Same box as the text fields and selects (h-10, border in the surface color). The library has no
   variable for the input height, only for its padding, so the height is set here. */
.dp__input {
    height: 2.5rem;
    padding-top: 0;
    padding-bottom: 0;
    border-color: rgb(var(--c-gray-900));
    @apply shadow-lg;
}

.dp__input:hover,
.dp__input:focus {
    border-color: var(--dp-border-color-focus);
}
</style>
